/* Technotrade Self-Quote Module — Phase 3
 * Generates a PDF quote in the browser using pdfmake, downloads instantly,
 * and POSTs the lead + line items to the GAS TA CRM endpoint (form_type=self_quote).
 *
 * Public API: window.TTQuote = {
 *   version,
 *   formatINR(n),
 *   generateQuoteId(),
 *   loadPdfMake(cb),
 *   showQuoteModal(quoteData, onSubmit),
 *   buildPdf(quoteData) -> docDefinition,
 *   downloadPdf(quoteData, filename),
 *   postToGAS(quoteData, gasEndpoint) -> Promise,
 *   submitQuote(quoteData, gasEndpoint) -> Promise  // does both
 * }
 *
 * quoteData shape:
 * {
 *   customer: { name, phone, email, company?, gstin? },
 *   items: [{ title, sku?, qty_label, qty_value, unit_price, total_price, url? }, ...],
 *   total_inr: <number>,
 *   source: 'pdp-coverage' | 'pdp-tile' | 'pdp-durafill' | 'standalone-calc',
 *   product_handle?, product_title?,
 *   page_url: window.location.href
 * }
 */
(function () {
  'use strict';

  var VERSION = '1.2.0';
  var PDFMAKE_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.7/pdfmake.min.js';
  var PDFMAKE_FONTS = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.7/vfs_fonts.js';

  /* ===== TT Canonical Company Info ===== */
  /* Always pulled from memory: company_technotrade_canonical.md */
  // CANONICAL — verified against memory/company_technotrade_canonical.md
  var TT_INFO = {
    name: 'Technotrade Associates',
    legalName: 'TECHNOTRADE ASSOCIATES',
    address: 'SB 114-A, Lal Kothi, Tonk Road, Jaipur - 302015, Rajasthan, India',
    gstin: '08AAJPC2902J1ZY',
    pan: 'AAJPC2902J',
    phone: '+91 92568 16832 / +91 97990 04515',
    email: 'query@technotradeassociates.com',
    website: 'store.technotrade.in',
    waBusiness: '92568 16832',  // Divya — Advisor (AI product support)
    waBusinessLabel: 'Divya (Advisor)',
    waSales: '97990 04515',     // Live Sales Support / RFQ
    waSalesLabel: 'Live Sales Support',
    crmPhone: '98297 79875'     // Used on payment / Proforma Invoice queries
  };

  var QUOTE_PARAMS = {
    validityDays: 5,
    paymentTerms: '100% advance against Proforma Invoice',
    freight: 'Extra — quoted on the Proforma Invoice (or you may arrange your own vehicle)',
    urgentAlertThreshold: 25000
  };

  /* ===== HELPERS ===== */

  function formatINR(n) {
    if (!isFinite(n) || n < 0) return '0';
    var v = Math.round(n);
    try { return v.toLocaleString('en-IN'); }
    catch (e) {
      var x = v.toString();
      if (x.length <= 3) return x;
      var last3 = x.slice(-3);
      var rest = x.slice(0, -3);
      return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
    }
  }

  function pad2(n) { return n < 10 ? '0' + n : '' + n; }

  function generateQuoteId() {
    var d = new Date();
    var y = d.getFullYear();
    var m = pad2(d.getMonth() + 1);
    var day = pad2(d.getDate());
    var seq = pad2(d.getHours()) + pad2(d.getMinutes());
    return 'EST-' + y + m + day + '-' + seq;
  }

  function dateAdd(date, days) {
    var d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  function fmtDate(d) {
    return pad2(d.getDate()) + ' ' +
      ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()] + ' ' +
      d.getFullYear();
  }

  /* ===== PDFMAKE LOADER ===== */

  var pdfmakeLoading = false;
  var pdfmakeReady = false;
  var loadCallbacks = [];

  function loadPdfMake(cb) {
    if (pdfmakeReady) { cb(); return; }
    loadCallbacks.push(cb);
    if (pdfmakeLoading) return;
    pdfmakeLoading = true;
    var s1 = document.createElement('script');
    s1.src = PDFMAKE_CDN;
    s1.onload = function () {
      var s2 = document.createElement('script');
      s2.src = PDFMAKE_FONTS;
      s2.onload = function () {
        pdfmakeReady = true;
        for (var i = 0; i < loadCallbacks.length; i++) loadCallbacks[i]();
        loadCallbacks = [];
      };
      document.head.appendChild(s2);
    };
    s1.onerror = function () {
      pdfmakeLoading = false;
      console.error('TTQuote: failed to load pdfmake');
    };
    document.head.appendChild(s1);
  }

  /* ===== PDF DOC DEFINITION =====
   * Returns a pdfmake docDefinition object.
   */
  function buildPdf(qd) {
    var now = new Date();
    var quoteId = qd.quoteId || generateQuoteId();
    var validUntil = dateAdd(now, QUOTE_PARAMS.validityDays);
    var isBoq = (qd.doc_type === 'boq');
    var docName = isBoq ? 'Bill of Quantities (BOQ)' : 'Instant Estimate';

    var subtotal = 0;
    for (var i = 0; i < qd.items.length; i++) {
      subtotal += qd.items[i].total_price || 0;
    }
    // GST is "extra as applicable" — varies by HSN. We do NOT pin a rate here.
    var grandTotal = subtotal;

    // Build items table
    var itemRows = [
      [
        { text: '#', style: 'thead' },
        { text: 'Product', style: 'thead' },
        { text: 'Quantity', style: 'thead', alignment: 'right' },
        { text: 'Rate (Rs/unit)', style: 'thead', alignment: 'right' },
        { text: 'Amount (Rs)', style: 'thead', alignment: 'right' }
      ]
    ];
    for (var j = 0; j < qd.items.length; j++) {
      var it = qd.items[j];
      itemRows.push([
        { text: (j + 1) + '.', style: 'cell' },
        {
          text: [
            { text: it.title || '-', style: 'cellTitle' },
            it.sku ? '\n' : '',
            it.sku ? { text: 'SKU: ' + it.sku, style: 'cellSub' } : '',
            it.note ? '\n' : '',
            it.note ? { text: it.note, style: 'cellSub' } : ''
          ],
          style: 'cell'
        },
        { text: it.qty_label || ((it.qty_value || 0) + ''), style: 'cell', alignment: 'right' },
        { text: it.by_others ? '—' : (it.unit_price ? formatINR(it.unit_price) : '-'), style: 'cell', alignment: 'right' },
        { text: it.by_others ? 'By others' : (it.total_price ? formatINR(it.total_price) : '-'), style: (it.by_others ? 'cellSub' : 'cellBold'), alignment: 'right' }
      ]);
    }

    return {
      pageSize: 'A4',
      pageMargins: [40, 60, 40, 80],
      info: {
        title: docName + ' ' + quoteId + ' — ' + TT_INFO.name,
        author: TT_INFO.name,
        subject: docName + ' from ' + TT_INFO.name + ', valid for ' + QUOTE_PARAMS.validityDays + ' days'
      },
      header: function (currentPage, pageCount) {
        return {
          margin: [40, 20, 40, 0],
          columns: [
            { text: TT_INFO.name.toUpperCase(), style: 'brandMark', alignment: 'left' },
            { text: 'Page ' + currentPage + ' of ' + pageCount, style: 'tinyMute', alignment: 'right' }
          ]
        };
      },
      footer: function (currentPage, pageCount) {
        return {
          margin: [40, 0, 40, 20],
          stack: [
            {
              canvas: [
                { type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: '#cbd5e1' }
              ],
              margin: [0, 0, 0, 6]
            },
            {
              columns: [
                { text: TT_INFO.address, style: 'tinyMute', width: '*' },
                { text: TT_INFO.website + ' · GSTIN ' + TT_INFO.gstin, style: 'tinyMute', alignment: 'right' }
              ]
            },
            {
              text: TT_INFO.name + ' · ' + docName + ' · Confirmed via Proforma Invoice from our Live Sales Support team.',
              style: 'tinyMute',
              alignment: 'center',
              margin: [0, 4, 0, 0]
            }
          ]
        };
      },
      content: [
        /* Title block — Technotrade Associates as brand, "Instant Estimate" as doc type */
        {
          columns: [
            {
              width: '*',
              stack: [
                { text: TT_INFO.name.toUpperCase(), style: 'brandTitle' },
                { text: docName, style: 'docSubtitle' },
                { text: TT_INFO.address, style: 'companyAddress', margin: [0, 8, 0, 0] },
                { text: 'GSTIN: ' + TT_INFO.gstin + '  ·  ' + TT_INFO.email, style: 'companyAddress' },
                { text: 'Phone: ' + TT_INFO.phone + '  ·  Web: ' + TT_INFO.website, style: 'companyAddress' }
              ]
            },
            {
              width: 180,
              stack: [
                { text: (isBoq ? 'BOQ No.' : 'Estimate No.'), style: 'metaLabel' },
                { text: quoteId, style: 'metaValue' },
                { text: 'Date', style: 'metaLabel', margin: [0, 6, 0, 0] },
                { text: fmtDate(now), style: 'metaValue' },
                { text: 'Valid Until', style: 'metaLabel', margin: [0, 6, 0, 0] },
                { text: fmtDate(validUntil) + '  (' + QUOTE_PARAMS.validityDays + ' days)', style: 'metaValueAccent' }
              ],
              alignment: 'right'
            }
          ],
          margin: [0, 0, 0, 18]
        },
        /* Bill-to block */
        {
          table: {
            widths: ['*'],
            body: [
              [
                {
                  stack: [
                    { text: 'PREPARED FOR', style: 'sectionLabel' },
                    { text: qd.customer.name || '-', style: 'billToName' },
                    {
                      text: [
                        qd.customer.phone ? ('Phone: ' + qd.customer.phone) : '',
                        qd.customer.email ? ((qd.customer.phone ? '   ·   ' : '') + qd.customer.email) : ''
                      ].join(''),
                      style: 'billToMeta'
                    },
                    qd.customer.company ? { text: 'Company: ' + qd.customer.company, style: 'billToMeta' } : '',
                    qd.customer.gstin ? { text: 'GSTIN: ' + qd.customer.gstin, style: 'billToMeta' } : ''
                  ],
                  margin: [10, 8, 10, 8],
                  fillColor: '#f4f6fb'
                }
              ]
            ]
          },
          layout: 'noBorders',
          margin: [0, 0, 0, 14]
        },
        /* Project / source */
        qd.product_title || qd.template_label ? {
          text: [
            { text: 'For: ', style: 'metaLabel' },
            { text: qd.product_title || qd.template_label || '', style: 'forProject' }
          ],
          margin: [0, 0, 0, 8]
        } : '',
        /* Items table */
        {
          table: {
            headerRows: 1,
            widths: [22, '*', 70, 70, 80],
            body: itemRows
          },
          layout: {
            hLineWidth: function (i, node) { return i === 0 || i === node.table.body.length ? 1 : 0.5; },
            vLineWidth: function () { return 0; },
            hLineColor: function (i, node) { return i === 0 || i === node.table.body.length ? '#1a2d5a' : '#e5e7eb'; },
            paddingTop: function () { return 7; },
            paddingBottom: function () { return 7; },
            paddingLeft: function (i) { return i === 0 ? 6 : 10; },
            paddingRight: function (i) { return 10; }
          },
          margin: [0, 0, 0, 12]
        },
        /* Totals block — GST is "extra as applicable", not pinned to 5% */
        {
          columns: [
            { text: '', width: '*' },
            {
              width: 260,
              table: {
                widths: ['*', 110],
                body: [
                  [
                    { text: 'Estimate Value (excl. GST)', style: 'grandLabel', alignment: 'left', fillColor: '#1a2d5a' },
                    { text: 'Rs ' + formatINR(grandTotal), style: 'grandValue', alignment: 'right', fillColor: '#1a2d5a' }
                  ],
                  [
                    { text: 'GST', style: 'totalsLabel', alignment: 'left' },
                    { text: 'Extra as applicable', style: 'totalsValue', alignment: 'right' }
                  ]
                ]
              },
              layout: {
                hLineWidth: function () { return 0.5; },
                vLineWidth: function () { return 0; },
                hLineColor: function () { return '#e5e7eb'; },
                paddingTop: function () { return 6; },
                paddingBottom: function () { return 6; },
                paddingLeft: function () { return 10; },
                paddingRight: function () { return 10; }
              }
            }
          ],
          margin: [0, 0, 0, 14]
        },
        /* Terms — confident, B2B-correct */
        {
          stack: [
            { text: 'TERMS & NOTES', style: 'sectionLabel', margin: [0, 10, 0, 4] },
            {
              ul: [
                { text: ['Validity: This Instant Estimate is valid for ', { text: QUOTE_PARAMS.validityDays + ' days', bold: true }, ' from the issue date above.'], style: 'termItem' },
                { text: ['Payment: ', { text: QUOTE_PARAMS.paymentTerms, bold: true }, '. Confirmed order is raised on our Proforma Invoice.'], style: 'termItem' },
                { text: ['GST: ', { text: 'Extra as applicable', bold: true }, ' (varies by HSN — reflected on the Proforma Invoice).'], style: 'termItem' },
                { text: ['Freight: ', { text: QUOTE_PARAMS.freight }], style: 'termItem' },
                { text: 'Stock availability and dispatch timelines are confirmed on the Proforma Invoice by our Live Sales Support team — typically within one working day.', style: 'termItem' }
              ].concat(qd.pricing_note ? [{ text: ['Pricing basis: ', { text: qd.pricing_note, bold: true }], style: 'termItem' }] : []).concat((qd.assumptions && qd.assumptions.length) ? [{ text: 'BASIS & ASSUMPTIONS', style: 'sectionLabel', margin: [0, 8, 0, 2] }].concat(qd.assumptions.map(function(a){ return { text: '• ' + a, style: 'termItem' }; })) : [])
            }
          ]
        },
        /* Contact CTA — fixed roles */
        {
          margin: [0, 14, 0, 0],
          fillColor: '#1a2d5a',
          table: {
            widths: ['*'],
            body: [[
              {
                stack: [
                  { text: 'Ready to convert this estimate to a confirmed order?', style: 'ctaTitle' },
                  { text: 'WhatsApp ' + TT_INFO.waBusiness + ' (' + TT_INFO.waBusinessLabel + ')  ·  ' + TT_INFO.waSales + ' (' + TT_INFO.waSalesLabel + ')', style: 'ctaSub' },
                  { text: 'Email: ' + TT_INFO.email + '  ·  Web: ' + TT_INFO.website, style: 'ctaSub' }
                ],
                margin: [12, 10, 12, 10],
                color: '#ffffff'
              }
            ]]
          },
          layout: 'noBorders'
        }
      ],
      styles: {
        brandMark: { fontSize: 9, color: '#94a3b8', characterSpacing: 1.2 },
        brandTitle: { fontSize: 20, bold: true, color: '#1a2d5a', characterSpacing: 0.6 },
        docSubtitle: { fontSize: 11, color: '#f5c61b', bold: true, characterSpacing: 1.5, margin: [0, 2, 0, 0] },
        docTitle: { fontSize: 22, bold: true, color: '#1a2d5a', characterSpacing: 0.5 },
        companyName: { fontSize: 11, bold: true, color: '#0f172a', margin: [0, 6, 0, 2] },
        companyAddress: { fontSize: 8.5, color: '#475569', margin: [0, 0, 0, 1] },
        metaLabel: { fontSize: 7.5, color: '#94a3b8', characterSpacing: 0.8 },
        metaValue: { fontSize: 11, bold: true, color: '#0f172a' },
        metaValueAccent: { fontSize: 11, bold: true, color: '#1a2d5a' },
        sectionLabel: { fontSize: 7.5, color: '#94a3b8', characterSpacing: 1, bold: true },
        billToName: { fontSize: 13, bold: true, color: '#0f172a', margin: [0, 4, 0, 2] },
        billToMeta: { fontSize: 9, color: '#475569', margin: [0, 0, 0, 1] },
        forProject: { fontSize: 9.5, bold: true, color: '#1a2d5a' },
        thead: { fontSize: 8.5, bold: true, color: '#1a2d5a', fillColor: '#f4f6fb' },
        cell: { fontSize: 9, color: '#1e293b' },
        cellTitle: { fontSize: 9, color: '#0f172a' },
        cellSub: { fontSize: 7.5, color: '#94a3b8' },
        cellBold: { fontSize: 9, bold: true, color: '#0f172a' },
        totalsLabel: { fontSize: 9, color: '#475569' },
        totalsValue: { fontSize: 10, bold: true, color: '#0f172a' },
        grandLabel: { fontSize: 10, bold: true, color: '#ffffff' },
        grandValue: { fontSize: 11, bold: true, color: '#f5c61b' },
        termItem: { fontSize: 8.5, color: '#1e293b', margin: [0, 2, 0, 0] },
        ctaTitle: { fontSize: 11, bold: true, color: '#f5c61b' },
        ctaSub: { fontSize: 9, color: '#ffffff', margin: [0, 3, 0, 0] },
        tinyMute: { fontSize: 7, color: '#94a3b8' }
      },
      defaultStyle: { font: 'Roboto' }
    };
  }

  /* ===== DOWNLOAD ===== */

  function downloadPdf(qd, filename) {
    var doc = buildPdf(qd);
    var fn = filename || ((qd.doc_type==='boq' ? 'TT-BOQ-' : 'TT-Estimate-') + (qd.quoteId || generateQuoteId()) + '.pdf');
    if (window.pdfMake) {
      window.pdfMake.createPdf(doc).download(fn);
    } else {
      loadPdfMake(function () { window.pdfMake.createPdf(doc).download(fn); });
    }
  }

  /* ===== GAS POST =====
   * Sends to existing TA CRM endpoint with form_type=self_quote.
   * Uses text/plain to bypass CORS preflight.
   */
  function postToGAS(qd, gasEndpoint) {
    return new Promise(function (resolve, reject) {
      if (!gasEndpoint) { reject(new Error('No GAS endpoint configured')); return; }
      var payload = {
        form_type: 'self_quote',
        quote_id: qd.quoteId || generateQuoteId(),
        timestamp: new Date().toISOString(),
        name: qd.customer.name,
        phone: qd.customer.phone,
        email: qd.customer.email,
        company: qd.customer.company || '',
        gstin: qd.customer.gstin || '',
        source: qd.source || 'unknown',
        product_handle: qd.product_handle || '',
        product_title: qd.product_title || qd.template_label || '',
        line_items_json: JSON.stringify(qd.items || []),
        subtotal_inr: qd.subtotal_inr || 0,
        gst_inr: qd.gst_inr || 0,
        total_inr: qd.total_inr || 0,
        urgent: (qd.total_inr || 0) >= QUOTE_PARAMS.urgentAlertThreshold ? 'yes' : 'no',
        source_url: window.location.href
      };
      var xhr = new XMLHttpRequest();
      xhr.open('POST', gasEndpoint, true);
      xhr.setRequestHeader('Content-Type', 'text/plain;charset=utf-8');
      xhr.onload = function () {
        if (xhr.status < 200 || xhr.status >= 300) { reject(new Error('HTTP ' + xhr.status)); return; }
        try {
          var r = JSON.parse(xhr.responseText);
          if (r.status === 'success' || r.result === 'success') resolve(r);
          else reject(new Error(r.message || 'GAS rejected'));
        } catch (e) { reject(e); }
      };
      xhr.onerror = function () { reject(new Error('Network error')); };
      xhr.send(JSON.stringify(payload));
    });
  }

  /* ===== ALL-IN-ONE: build PDF + download + POST =====
   * Returns Promise. Resolves when both PDF download AND GAS POST complete.
   * PDF download starts immediately even if GAS POST fails.
   */
  /* Klaviyo lead bridge (2026-07-28): identify + 'Estimator Lead' metric via onsite object */
  function ttKlaviyoLead(qd) {
    try {
      var c = (qd && qd.customer) || {};
      if (!c.email) return;
      var _k = window.klaviyo || window._learnq || [];
      var parts = (c.name || '').trim().split(/\s+/);
      var ph = String(c.phone || '').replace(/\D/g, '').slice(-10);
      _k.push(['identify', { '$email': c.email, '$first_name': parts[0] || '', '$last_name': parts.slice(1).join(' '), '$phone_number': ph ? ('+91' + ph) : '', 'Company': c.company || '', 'GSTIN': c.gstin || '', 'Latest Estimator Template': qd.product_title || qd.template_label || '' }]);
      _k.push(['track', 'Estimator Lead', { 'Template': qd.product_title || qd.template_label || '', 'Source': qd.source || 'quote', 'Quote ID': qd.quoteId || '', 'Estimate Value INR': qd.total_inr || 0, 'URL': window.location.href }]);
    } catch (e) {}
  }

  function submitQuote(qd, gasEndpoint) {
    qd.quoteId = qd.quoteId || ((qd.doc_type==='boq') ? generateQuoteId().replace('EST-','BOQ-') : generateQuoteId());
    // Compute totals if missing — GST is "extra as applicable", so total = subtotal
    if (!qd.subtotal_inr) {
      var sub = 0;
      for (var i = 0; i < (qd.items || []).length; i++) sub += (qd.items[i].total_price || 0);
      qd.subtotal_inr = sub;
      qd.gst_inr = 0; // GST not pinned; CRM column kept for back-compat
      qd.total_inr = sub;
    }
    ttKlaviyoLead(qd);
    // Download PDF (loads pdfmake if needed)
    downloadPdf(qd);
    // POST to GAS in parallel (background)
    return postToGAS(qd, gasEndpoint);
  }

  /* ===== MODAL FORM =====
   * Lightweight customer-info modal. Calls onSubmit({name, phone, email, company, gstin}) when valid.
   */
  function showQuoteModal(prefill, onSubmit) {
    prefill = prefill || {};
    // Remove any existing
    var ex = document.getElementById('tt-quote-modal');
    if (ex) ex.parentNode.removeChild(ex);

    var html = '' +
      '<div id="tt-quote-modal" class="ttq-modal-bg">' +
      '  <div class="ttq-modal">' +
      '    <div class="ttq-modal-h">' +
      '      <div class="ttq-modal-title">Generate Instant Estimate</div>' +
      '      <button type="button" class="ttq-modal-close" aria-label="Close">×</button>' +
      '    </div>' +
      '    <div class="ttq-modal-body">' +
      '      <p class="ttq-modal-lead">Your 5-day Instant Estimate downloads instantly and a copy is emailed to you. Our Live Sales Support team will follow up with a Proforma Invoice to confirm pricing, GST and freight.</p>' +
      '      <div class="ttq-fld">' +
      '        <label>Your name <span class="req">*</span></label>' +
      '        <input type="text" data-fld="name" value="' + escAttr(prefill.name || '') + '" placeholder="Full name">' +
      '      </div>' +
      '      <div class="ttq-row">' +
      '        <div class="ttq-fld">' +
      '          <label>Phone <span class="req">*</span></label>' +
      '          <input type="tel" data-fld="phone" value="' + escAttr(prefill.phone || '') + '" placeholder="10-digit mobile" maxlength="10">' +
      '        </div>' +
      '        <div class="ttq-fld">' +
      '          <label>Email <span class="req">*</span></label>' +
      '          <input type="email" data-fld="email" value="' + escAttr(prefill.email || '') + '" placeholder="you@example.com">' +
      '        </div>' +
      '      </div>' +
      '      <div class="ttq-row">' +
      '        <div class="ttq-fld">' +
      '          <label>Company <span class="opt">(optional)</span></label>' +
      '          <input type="text" data-fld="company" value="' + escAttr(prefill.company || '') + '">' +
      '        </div>' +
      '        <div class="ttq-fld">' +
      '          <label>GSTIN <span class="opt">(optional)</span></label>' +
      '          <input type="text" data-fld="gstin" value="' + escAttr(prefill.gstin || '') + '" placeholder="22AAAAA0000A1Z5" maxlength="15">' +
      '        </div>' +
      '      </div>' +
      '      <div class="ttq-err" data-err></div>' +
      '      <button type="button" class="ttq-submit">Download Instant Estimate</button>' +
      '      <p class="ttq-fine">Final pricing, GST and freight confirmed on the Proforma Invoice from our Live Sales Support team.</p>' +
      '    </div>' +
      '  </div>' +
      '</div>';

    var wrap = document.createElement('div');
    wrap.innerHTML = html;
    document.body.appendChild(wrap.firstChild);

    var modal = document.getElementById('tt-quote-modal');
    function close() { modal.parentNode.removeChild(modal); }

    modal.addEventListener('click', function (e) {
      if (e.target === modal) close();
    });
    modal.querySelector('.ttq-modal-close').addEventListener('click', close);

    var submitBtn = modal.querySelector('.ttq-submit');
    var errEl = modal.querySelector('[data-err]');
    submitBtn.addEventListener('click', function () {
      var data = {};
      ['name','phone','email','company','gstin'].forEach(function (k) {
        var el = modal.querySelector('[data-fld="' + k + '"]');
        data[k] = el ? el.value.trim() : '';
      });
      // Validate
      if (!data.name) { errEl.textContent = 'Please enter your name.'; return; }
      if (!/^\d{10}$/.test(data.phone)) { errEl.textContent = '10-digit mobile number please.'; return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) { errEl.textContent = 'Please enter a valid email.'; return; }
      if (data.gstin && data.gstin.length > 0 && data.gstin.length !== 15) { errEl.textContent = 'GSTIN must be 15 characters (or leave blank).'; return; }
      errEl.textContent = '';
      submitBtn.disabled = true;
      submitBtn.innerHTML = 'Generating PDF…';
      try {
        onSubmit(data, close);
      } catch (e) {
        errEl.textContent = 'Error: ' + e.message;
        submitBtn.disabled = false;
        submitBtn.textContent = 'Download Instant Estimate';
      }
    });

    // Focus name field
    setTimeout(function () { var n = modal.querySelector('[data-fld="name"]'); if (n) n.focus(); }, 50);
  }

  function escAttr(s) {
    return (s == null ? '' : ('' + s)).replace(/[&<>"']/g, function (c) {
      return { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c];
    });
  }

  /* ===== INJECT MODAL CSS ===== */
  function injectCss() {
    if (document.getElementById('tt-quote-css')) return;
    var css = '' +
      '.ttq-modal-bg{position:fixed;inset:0;background:rgba(15,23,42,.55);z-index:99998;display:flex;align-items:center;justify-content:center;padding:20px;animation:ttqFade .15s ease;font-family:inherit}' +
      '.ttq-modal{background:#fff;border-radius:14px;box-shadow:0 16px 60px rgba(15,23,42,.25);width:100%;max-width:520px;max-height:92vh;overflow:auto;animation:ttqRise .2s ease}' +
      '.ttq-modal-h{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #eef0f3;position:sticky;top:0;background:#fff;z-index:1}' +
      '.ttq-modal-title{font-size:1.1rem;font-weight:800;color:#1a2d5a;letter-spacing:-.01em}' +
      '.ttq-modal-close{background:none;border:none;font-size:1.5rem;line-height:1;color:#94a3b8;cursor:pointer;padding:0 4px;font-family:inherit}' +
      '.ttq-modal-close:hover{color:#0f172a}' +
      '.ttq-modal-body{padding:18px 22px 22px}' +
      '.ttq-modal-lead{font-size:.85rem;color:#475569;margin:0 0 14px;line-height:1.5}' +
      '.ttq-fld{margin-bottom:12px}' +
      '.ttq-fld label{display:block;font-size:.78rem;font-weight:600;color:#334155;margin-bottom:5px}' +
      '.ttq-fld label .req{color:#dc2626}' +
      '.ttq-fld label .opt{color:#94a3b8;font-weight:400}' +
      '.ttq-fld input{width:100%;padding:9px 12px;border:1.5px solid #e5e7eb;border-radius:8px;font-size:.92rem;font-family:inherit;color:#0f172a;background:#fff;outline:none;transition:border-color .15s;box-sizing:border-box}' +
      '.ttq-fld input:focus{border-color:#1a2d5a;box-shadow:0 0 0 3px rgba(26,45,90,.08)}' +
      '.ttq-row{display:grid;grid-template-columns:1fr 1fr;gap:10px}' +
      '@media(max-width:480px){.ttq-row{grid-template-columns:1fr}}' +
      '.ttq-err{font-size:.8rem;color:#dc2626;margin:8px 0;min-height:1.1em}' +
      '.ttq-submit{display:block;width:100%;padding:12px 16px;background:#1a2d5a;color:#fff;border:none;border-radius:8px;font-size:.95rem;font-weight:700;cursor:pointer;font-family:inherit;transition:background .15s}' +
      '.ttq-submit:hover{background:#253f7a}' +
      '.ttq-submit:disabled{opacity:.6;cursor:not-allowed}' +
      '.ttq-fine{font-size:.7rem;color:#94a3b8;text-align:center;margin:10px 0 0;line-height:1.4}' +
      '@keyframes ttqFade{from{opacity:0}to{opacity:1}}' +
      '@keyframes ttqRise{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}';
    var style = document.createElement('style');
    style.id = 'tt-quote-css';
    style.textContent = css;
    document.head.appendChild(style);
  }

  /* ===== ON LOAD ===== */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectCss);
  } else {
    injectCss();
  }

  /* ===== EXPORT ===== */
  window.TTQuote = {
    version: VERSION,
    TT_INFO: TT_INFO,
    QUOTE_PARAMS: QUOTE_PARAMS,
    formatINR: formatINR,
    generateQuoteId: generateQuoteId,
    loadPdfMake: loadPdfMake,
    buildPdf: buildPdf,
    downloadPdf: downloadPdf,
    postToGAS: postToGAS,
    submitQuote: submitQuote,
    showQuoteModal: showQuoteModal
  };
})();
